import React, { useEffect, useState } from "react";
import api from "../utils/axios";

interface WalletUser {
  uid: string;
  name: string;
  inr: number;
  gc?: number;
  sc?: number;
  active_currency?: string;
}

interface ActionModalState {
  isOpen: boolean;
  user: WalletUser | null;
  amount: string;
  type: "deposit" | "withdraw";
  reason: string;
}

const BonusAndPanelty: React.FC = () => {
  const [users, setUsers] = useState<WalletUser[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [targetCurrency, setTargetCurrency] = useState<"gc" | "sc">("gc");

  const [debitValues, setDebitValues] = useState<Record<string, string>>({});
  const [creditValues, setCreditValues] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);

  // Safeguard Confirmation Modal state
  const [actionModal, setActionModal] = useState<ActionModalState>({
    isOpen: false,
    user: null,
    amount: "",
    type: "deposit",
    reason: "",
  });
  const [submitting, setSubmitting] = useState(false);

  /* ---------------- Fetch Wallet Data ---------------- */
  const fetchWallet = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    setLoading(true);

    try {
      const res = await api.get("/getwallet", {
        params: {
          page,
          limit,
          search: searchTerm,
        },
      });

      setUsers(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error("Wallet fetch error:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWallet();
  }, [page, searchTerm]);

  /* ---------------- Input Handling ---------------- */
  const handleInputChange = (
    uid: string,
    value: string,
    type: "debit" | "credit"
  ) => {
    if (type === "debit") {
      setDebitValues((prev) => ({ ...prev, [uid]: value }));
    } else {
      setCreditValues((prev) => ({ ...prev, [uid]: value }));
    }
  };

  /* ---------------- Open Confirmation Modal ---------------- */
  const promptBalanceUpdate = (
    user: WalletUser,
    amount: string,
    type: "deposit" | "withdraw"
  ) => {
    if (!amount || parseFloat(amount) <= 0) {
      setMessage({
        text: "Please enter a valid amount greater than 0",
        type: "error",
      });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    const currentBal = targetCurrency === "gc" ? (user.gc ?? 10000) : (user.sc ?? user.inr ?? 0);
    if (type === "withdraw" && parseFloat(amount) > currentBal) {
      setMessage({
        text: `Cannot deduct ${amount} ${targetCurrency.toUpperCase()} - User only has ${currentBal.toFixed(2)} ${targetCurrency.toUpperCase()}`,
        type: "error",
      });
      setTimeout(() => setMessage(null), 4000);
      return;
    }

    setActionModal({
      isOpen: true,
      user,
      amount,
      type,
      reason: "",
    });
  };

  /* ---------------- Confirm and Execute Balance Update ---------------- */
  const handleConfirmAction = async () => {
    const { user, amount, type, reason } = actionModal;
    if (!user || !amount || parseFloat(amount) <= 0) return;

    if (type === "withdraw" && (!reason || reason.trim().length < 5)) {
      alert("A clear reason (at least 5 characters) is strictly mandatory for issuing penalties/deductions.");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) return;

    setSubmitting(true);
    try {
      await api.post(
        "/updatebalance",
        {
          uid: user.uid,
          coin: targetCurrency,
          amount: parseFloat(amount),
          type,
          reason: reason.trim(),
        },
        { headers: { Authorization: token } }
      );

      setMessage({
        text: `Successfully ${type === "deposit" ? "credited bonus of" : "debited penalty of"} ${amount} ${targetCurrency.toUpperCase()} for ${user.name} (#${user.uid})!`,
        type: "success",
      });
      setTimeout(() => setMessage(null), 4000);

      setActionModal({ isOpen: false, user: null, amount: "", type: "deposit", reason: "" });
      setDebitValues((prev) => ({ ...prev, [user.uid]: "" }));
      setCreditValues((prev) => ({ ...prev, [user.uid]: "" }));
      fetchWallet();
    } catch (error: any) {
      console.error("Balance update error:", error);
      setMessage({
        text: error?.response?.data?.error || error?.response?.data?.message || "Failed to update balance",
        type: "error",
      });
      setTimeout(() => setMessage(null), 4000);
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Bonus and Penalty Management
          </h1>
          <p className="text-sm text-gray-500">
            Safely credit promotional bonuses or issue audited penalties with confirmation safeguards.
          </p>
        </div>

        {/* Currency Switcher Pill */}
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
            Target Currency:
          </span>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
            <button
              type="button"
              onClick={() => setTargetCurrency("gc")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                targetCurrency === "gc"
                  ? "bg-amber-500 text-white shadow"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <span>🪙</span> Gold Coins (GC)
            </button>
            <button
              type="button"
              onClick={() => setTargetCurrency("sc")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                targetCurrency === "sc"
                  ? "bg-sky-600 text-white shadow"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <span>✨</span> Sweep Coins (SC)
            </button>
          </div>
          <span className="text-sm text-gray-500 font-medium">
            Total Users: {total}
          </span>
        </div>
      </div>

      {message && (
        <div
          className={`mb-4 p-3 rounded-lg text-sm font-semibold border ${
            message.type === "success"
              ? "bg-green-50 text-green-800 border-green-200"
              : "bg-red-50 text-red-800 border-red-200"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Search */}
      <div className="mb-6 max-w-md">
        <input
          className="w-full rounded-lg border px-4 py-2 focus:ring-2 focus:ring-indigo-500"
          placeholder="Search by name or UID"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              {[
                "S.No",
                "Name",
                "UID",
                "Gold Coins (GC)",
                "Sweep Coins (SC)",
                "Active Mode",
                `Penalty (${targetCurrency.toUpperCase()})`,
                `Bonus (${targetCurrency.toUpperCase()})`,
              ].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="py-10 text-center text-gray-500">
                  Loading wallet data...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-10 text-center text-gray-500">
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user, index) => (
                <tr
                  key={user.uid}
                  className="border-t hover:bg-gray-50"
                >
                  <td className="px-4 py-3">
                    {(page - 1) * limit + index + 1}
                  </td>
                  <td className="px-4 py-3 font-medium">
                    {user.name}
                  </td>
                  <td className="px-4 py-3">{user.uid}</td>
                  <td className="px-4 py-3 font-bold text-amber-700 whitespace-nowrap">
                    🪙 {Number(user.gc ?? 10000).toLocaleString()} GC
                  </td>
                  <td className="px-4 py-3 font-bold text-sky-700 whitespace-nowrap">
                    ✨ {Number(user.sc ?? user.inr ?? 0).toFixed(2)} SC
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                      user.active_currency === "sc"
                        ? "bg-sky-100 text-sky-800 border border-sky-200"
                        : "bg-amber-100 text-amber-800 border border-amber-200"
                    }`}>
                      {user.active_currency === "sc" ? "✨ SC" : "🪙 GC"}
                    </span>
                  </td>

                  {/* Debit / Penalty */}
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="0"
                        step="any"
                        className="w-24 border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-red-500"
                        placeholder={targetCurrency.toUpperCase()}
                        value={debitValues[user.uid] || ""}
                        onChange={(e) =>
                          handleInputChange(
                            user.uid,
                            e.target.value,
                            "debit"
                          )
                        }
                      />
                      <button
                        onClick={() =>
                          promptBalanceUpdate(
                            user,
                            debitValues[user.uid],
                            "withdraw"
                          )
                        }
                        className="bg-red-500 text-white px-3 py-1 rounded text-xs font-bold hover:bg-red-600 transition shadow-sm"
                      >
                        Penalty
                      </button>
                    </div>
                  </td>

                  {/* Credit / Bonus */}
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="0"
                        step="any"
                        className="w-24 border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-green-500"
                        placeholder={targetCurrency.toUpperCase()}
                        value={creditValues[user.uid] || ""}
                        onChange={(e) =>
                          handleInputChange(
                            user.uid,
                            e.target.value,
                            "credit"
                          )
                        }
                      />
                      <button
                        onClick={() =>
                          promptBalanceUpdate(
                            user,
                            creditValues[user.uid],
                            "deposit"
                          )
                        }
                        className="bg-green-600 text-white px-3 py-1 rounded text-xs font-bold hover:bg-green-700 transition shadow-sm"
                      >
                        Bonus
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-6">
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </span>

          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Prev
            </button>
            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Safeguard Modal */}
      {actionModal.isOpen && actionModal.user && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200">
            <div className="flex items-center gap-3 mb-4">
              <span className={`p-3 rounded-full text-xl ${actionModal.type === "withdraw" ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"}`}>
                {actionModal.type === "withdraw" ? "⚠️" : "🎁"}
              </span>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {actionModal.type === "withdraw" ? "Confirm Balance Penalty" : "Confirm Bonus Credit"}
                </h3>
                <p className="text-xs text-gray-500">
                  {actionModal.type === "withdraw"
                    ? "Safeguard check: Verify details before debiting funds"
                    : "Assign promotional bonus to player"}
                </p>
              </div>
            </div>

            {actionModal.type === "withdraw" && (
              <div className="p-3 mb-4 rounded-xl bg-red-50 border border-red-200 text-xs text-red-800 font-medium">
                🚨 <strong>Warning:</strong> This action will directly deduct <strong>{actionModal.amount} {targetCurrency.toUpperCase()}</strong> from the player's account. This creates an immutable audit trail.
              </div>
            )}

            <div className="space-y-3 mb-5 text-sm">
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Player:</span>
                <span className="font-semibold text-gray-800">{actionModal.user.name}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">User ID (UID):</span>
                <span className="font-mono text-gray-700">{actionModal.user.uid}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Current Balance:</span>
                <span className="font-bold text-gray-800">
                  {targetCurrency === "gc"
                    ? `🪙 ${(actionModal.user.gc ?? 10000).toLocaleString()} GC`
                    : `✨ ${(actionModal.user.sc ?? actionModal.user.inr ?? 0).toFixed(2)} SC`}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Amount to {actionModal.type === "withdraw" ? "Deduct" : "Credit"}:</span>
                <span className={`font-black text-base ${actionModal.type === "withdraw" ? "text-red-600" : "text-green-600"}`}>
                  {actionModal.type === "withdraw" ? "-" : "+"}{actionModal.amount} {targetCurrency.toUpperCase()}
                </span>
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                Reason / Justification {actionModal.type === "withdraw" ? "(Mandatory)" : "(Optional)"}
              </label>
              <textarea
                rows={2}
                className="w-full border rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-indigo-500"
                placeholder={
                  actionModal.type === "withdraw"
                    ? "e.g., Chargeback settlement, duplicate bonus clawback, ticket #1029..."
                    : "e.g., VIP loyalty bonus, tournament prize..."
                }
                value={actionModal.reason}
                onChange={(e) =>
                  setActionModal((prev) => ({ ...prev, reason: e.target.value }))
                }
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                disabled={submitting}
                onClick={() => setActionModal({ isOpen: false, user: null, amount: "", type: "deposit", reason: "" })}
                className="px-4 py-2 border rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting || (actionModal.type === "withdraw" && actionModal.reason.trim().length < 5)}
                onClick={handleConfirmAction}
                className={`px-5 py-2 rounded-lg text-sm font-bold text-white shadow transition disabled:opacity-50 ${
                  actionModal.type === "withdraw" ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"
                }`}
              >
                {submitting
                  ? "Processing..."
                  : actionModal.type === "withdraw"
                  ? "Confirm & Apply Penalty"
                  : "Confirm & Credit Bonus"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BonusAndPanelty;
